import { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { formatDate, formatNumber, formatPercent } from "@/utils/format";
import type { InstagramDayPoint } from "@/services/marketing";
import { lineDots, TOOLTIP_CURSOR_BAND } from "@/utils/rechartsInteraction";

interface Props {
  data: InstagramDayPoint[];
  loading?: boolean;
}

interface EngagementDayRow extends InstagramDayPoint {
  label: string;
  engagementRate: number | null;
  engagementActions: number;
}

function formatChartDate(iso: string): string {
  const raw = (iso ?? "").trim().split("T")[0] ?? "";
  const parts = raw.split("-");
  if (parts.length < 3) return raw;
  return `${parts[2]}/${parts[1]}`;
}

function dailyEngagementActions(row: InstagramDayPoint): number {
  return row.likes + row.comments + row.shares + row.saves + (row.replies ?? 0);
}

function dailyEngagementRate(row: InstagramDayPoint): number | null {
  if (row.reach <= 0) return null;
  return (dailyEngagementActions(row) / row.reach) * 100;
}

function EngagementTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload?: EngagementDayRow }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="min-w-[220px] rounded-xl border border-slate-200 bg-white p-3 shadow-modal">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {formatDate(row.date)}
      </p>
      <p className="text-sm font-semibold text-teal-700">
        Taxa de engajamento:{" "}
        {row.engagementRate == null ? "—" : formatPercent(row.engagementRate, 2)}
      </p>
      <div className="mt-2 space-y-0.5 text-xs text-slate-600">
        <p>Alcance: {formatNumber(row.reach)}</p>
        <p>Engajamentos: {formatNumber(row.engagementActions)}</p>
        <p className="pt-1 text-slate-500">
          Curtidas {formatNumber(row.likes)} · Coment. {formatNumber(row.comments)} · Encam.{" "}
          {formatNumber(row.shares)} · Salv. {formatNumber(row.saves)} · Resp.{" "}
          {formatNumber(row.replies ?? 0)}
        </p>
      </div>
    </div>
  );
}

export function InstagramEngagementRateByDayChart({ data, loading }: Props) {
  const chartData = useMemo<EngagementDayRow[]>(
    () =>
      data.map((row) => ({
        ...row,
        label: formatChartDate(row.date),
        engagementActions: dailyEngagementActions(row),
        engagementRate: dailyEngagementRate(row),
      })),
    [data],
  );

  const averageRate = useMemo(() => {
    const valid = chartData.filter((row) => row.engagementRate != null);
    if (valid.length === 0) return null;
    const totalReach = valid.reduce((sum, row) => sum + row.reach, 0);
    const totalEngagement = valid.reduce((sum, row) => sum + row.engagementActions, 0);
    if (totalReach <= 0) return null;
    return (totalEngagement / totalReach) * 100;
  }, [chartData]);

  const hasEngagementData = chartData.some((row) => row.engagementRate != null);

  return (
    <ChartContainer
      title="Taxa de engajamento por dia"
      subtitle="(Curtidas + comentários + encaminhamentos + salvamentos) ÷ alcance — totais do dia na API"
      loading={loading}
      empty={!loading && !hasEngagementData}
      emptyMessage="Sem dados de engajamento diário no período."
    >
      <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
        <YAxis
          tick={{ fontSize: 11, fill: "#64748b" }}
          width={52}
          tickFormatter={(v) => `${v}%`}
          domain={[0, "auto"]}
        />
        <Tooltip content={<EngagementTooltip />} cursor={TOOLTIP_CURSOR_BAND} />
        <Legend formatter={() => "Taxa de engajamento"} />
        {averageRate != null && (
          <ReferenceLine
            y={averageRate}
            stroke="#94a3b8"
            strokeDasharray="4 4"
            label={{
              value: `Média ${formatPercent(averageRate, 1)}`,
              position: "insideTopRight",
              fill: "#64748b",
              fontSize: 11,
            }}
          />
        )}
        <Line
          type="monotone"
          dataKey="engagementRate"
          name="engagementRate"
          stroke="#0d9488"
          strokeWidth={2}
          connectNulls={false}
          {...lineDots("#0d9488")}
        />
      </LineChart>
    </ChartContainer>
  );
}
